/**
 * Isomorphic helpers for the Returns module.
 * Loaded server-side via `require(./returnsUtils)` and client-side via `this.$instance('returnsUtils')`.
 */
const _ = require('lodash');
const Big = require('big.js');


/**
* Human-readable labels for `returnType`.
*
* ── ADD MISSING VALUES HERE ──
* One entry per value, keyed by the exact string the payload carries. Anything absent falls through
* to the raw value (see returnTypeLabel), so an unmapped type is ugly, never lost.
*
* That fallback is the whole design. `returnType` is deliberately NOT a schema enum — SURP owns this
* vocabulary and can add to it without telling us, and a strict list would drop the lodgement rather
* than record it (see the schema comment in returns.doop). This table is presentation only: it never
* validates, rejects, or rewrites what is stored.
*
* Sources, all verified rather than guessed:
*  - SURP's own form: public/partials/abi-returns-25-public-display.php (the `return_method` radios)
*  - SURP's staff Process Return page: the staff_* variants
*  - The Monday step's own map: docs/pipedream/au/monday-create-item.js
*
* NOTE the paid-label entries stay carrier-neutral. Monday's map calls them "AusPost", which is true
* for AU only — UK runs DPD and NZ has no paid label at all. The display text is also an admin
* setting per region (`pay_method_one_text`), so naming a carrier here would be wrong somewhere.
*/
const returnTypeLabels = {
	// Customer-facing web form
	pay_method_one:      'Paid return label (option 1)',
	pay_method_two:      'Paid return label (option 2)',
	own_courier:         'Customer\'s own courier',
	direct_return:       'Direct return to warehouse',
	// SURP staff "Process Return" page
	staff_full_return:   'Full return (staff lodged)',
	staff_local_dropoff: 'In-person drop-off',
	staff_label_only:    'Label reprint only',
	// The return form's hidden field ships with value="ap" and is only overwritten once a method is
	// chosen, so an untouched submission can still arrive as this. Mapped so it reads as the
	// fallback it is rather than as a real choice.
	ap:                  'Unspecified (form default)',
};


/**
* Human-readable labels for `status`.
*
* The stored values are not what anyone should read. `submitted` in particular means "the goods
* have ARRIVED" — the opposite of what the word suggests, and directly confusable with `lodged`,
* which is when the return was actually submitted. The name survives because the value is indexed,
* filtered on and rendered across the list and edit views; the display does not have to inherit it.
*
* "Arrived" rather than "Received" so AOS, the Monday board's Status column and the button that
* sets it all say the same word.
*/
const returnStatusLabels = {
	draft: 'Draft',
	lodged: 'Lodged',
	submitted: 'Arrived',
	qcCompleted: 'QC Completed',
	finalized: 'Finalized',
	failed: 'Failed',
};


/**
* The lifecycle in order, for anything that needs to reason about progress rather than just render
* a label — a filter that wants the steps listed sensibly, or a check for "past this point".
*
* 'failed' is deliberately absent: it is not a stage, it is a lodgement that did not happen, and
* putting it in sequence would imply a return passes through it.
*/
const returnStatusOrder = ['draft', 'lodged', 'submitted', 'qcCompleted', 'finalized'];


/**
* Render a return `status` for a human.
*
* Falls through to the raw value for anything unmapped, on the same principle as returnTypeLabel:
* a status this table has not caught up with should look odd, not invisible.
*
* @param {String} value The stored status
* @param {String} [fallback] What to show when the status is empty entirely
*
* @returns {String} The mapped label, or the raw value when it isn't in the table
*/
function returnStatusLabel(value, fallback = '—') {
	const raw = String(value || '').trim();
	if (!raw) return fallback;
	// Exact match first, then a case-insensitive sweep. Lowercasing the lookup key outright — which
	// this used to do — silently broke the moment a camelCase status was added: 'qcCompleted'
	// became 'qccompleted', matched nothing, and rendered as itself.
	if (returnStatusLabels[raw]) return returnStatusLabels[raw];
	const found = Object.keys(returnStatusLabels).find(k => k.toLowerCase() === raw.toLowerCase());
	return found ? returnStatusLabels[found] : value;
}


/**
* Render a `returnType` for a human.
*
* @param {String} value The stored returnType
* @param {String} [fallback] What to show when the value is empty entirely
*
* @returns {String} The mapped label, or the raw value when it isn't in the table
*/
function returnTypeLabel(value, fallback = '—') {
	const key = String(value || '').trim().toLowerCase();
	if (!key) return fallback;
	return returnTypeLabels[key] || value;
}


/**
* Escape a string for use inside a RegExp
*/
function escapeRegExp(str) {
	return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
* Greedy "same-order-first" picker.
* Given a list of orders (most recent first) and a list of requested SKUs, picks the highest-scoring order,
* records its matched SKUs, removes them from the requested list, then loops on the remainder.
*
* @param {Array<Object>} orders Orders to consider (assumed sorted desc by finalised)
* @param {Array<String>} skus Requested SKUs
* @returns {{candidates: Array<{order: Object, matchedItems: Array<Object>}>, unmatchedSkus: Array<String>}}
*/
function findBestMatches(orders, skus) {
	const norm = s => String(s || '').trim().toLowerCase();
	const requested = _.uniq((skus || []).map(s => String(s).trim()).filter(Boolean));
	const requestedLower = new Set(requested.map(norm));

	const candidates = (orders || [])
		.map(order => {
			const matchedItems = (order.items || []).filter(i => i.sku && requestedLower.has(norm(i.sku)));
			const finalised = order.finalised ? new Date(order.finalised).getTime() : 0;
			const created = order.created ? new Date(order.created).getTime() : 0;
			return {order, matchedItems, recency: finalised || created};
		})
		.filter(c => c.matchedItems.length > 0)
		.sort((a, b) => b.recency - a.recency)
		.map(({order, matchedItems}) => ({order, matchedItems}));

	const matchedSkusLower = new Set();
	candidates.forEach(c => c.matchedItems.forEach(i => matchedSkusLower.add(norm(i.sku))));
	const unmatchedSkus = requested.filter(s => !matchedSkusLower.has(norm(s)));

	return {candidates, unmatchedSkus};
}


/**
* Compute the next return ref of the form `RA-<instance>-<wooID|orderRef>-NN`.
* Requires `db` to be available (server-side only).
*
* @param {Object} opts
* @param {Object} opts.db Mongoosy db object
* @param {String} opts.instance Instance code (AU/NZ/UK)
* @param {String} opts.orderRef Source order ref (fallback when no wooID present)
* @param {String} [opts.wooID] WooCommerce numeric ID for the source order
* @returns {Promise<String>}
*/
function nextReturnNumber({db, instance, orderRef, wooID}) {
	const ident = wooID || orderRef;
	const prefix = `RA-${instance}-${ident}-`;
	const re = new RegExp(`^${escapeRegExp(prefix)}(\\d+)$`);

	return db.returns
		.find({ref: {$regex: `^${escapeRegExp(prefix)}`}})
		.select('ref')
		.lean()
		.then(existing => {
			const maxN = _.max(existing.map(r => {
				const m = re.exec(r.ref);
				return m ? parseInt(m[1], 10) : 0;
			})) || 0;
			return `${prefix}${String(maxN + 1).padStart(2, '0')}`;
		});
}


/**
* Build the external returns payload from a Return document, its source orders, and the return reasons list.
*
* @param {Object} returnDoc The (plain) return document
* @param {Object} ordersById Map of {orderId: orderDoc}
* @param {Object} [reasonsList] The list document with `items[{title, value}]` used to resolve reason text
* @param {Object} [options] Additional options to mutate behaviour
* @param {String} [options.adminEmail] Recipient of the internal notification, from
*        `config.returns.adminEmail[instance]`. Passed in rather than read here because this module
*        is isomorphic and has no access to app.config. Blank/absent = no internal notification.
* @returns {Object}
*/
function buildExternalPayload(returnDoc, ordersById, reasonsList, options) {
	const settings = {adminEmail: '', ...options};
	const reasons = ((reasonsList || {}).items || []);
	const reasonByValue = _.keyBy(reasons, 'value');

	const firstItem = (returnDoc.items || [])[0];
	const firstOrder = firstItem ? ordersById[String(firstItem.orderId)] : null;
	const wooID = firstOrder && firstOrder.externalIds && firstOrder.externalIds.woo
		? Number(firstOrder.externalIds.woo)
		: null;

	// Only include items the operator actually selected to return (qty > 0)
	const returnableItems = (returnDoc.items || []).filter(i => (Number(i.quantity) || 0) > 0);

	const uniqueOrderIds = _.uniq(returnableItems.map(i => String(i.orderId)));
	const returnInstance = uniqueOrderIds.length || 1;

	const totalWeight = returnableItems.reduce(
		(acc, i) => acc + (Number(i.weight) || 0) * (Number(i.quantity) || 0),
		0,
	);

	const address = returnDoc.address || {};
	const customerAddress = [
		address.address1,
		address.city,
		address.state,
		address.postcode,
		address.country,
	].filter(Boolean).join(', ');

	// MUST NOT default to 'SURP': SURP's Pipedream intake step skips origin=AOS to avoid posting
	// our own submissions back to us, and a return that claims to be SURP would round-trip.
	const origin = returnDoc.origin || 'AOS';

	// Format numbers as 2dp strings (matching real-world example payload)
	const money = v => (Number(v) || 0).toFixed(2);
	const num = v => String(Number(v) || 0);

	const productsInfo = returnableItems.map(i => {
		const reasonText = i.reasonText || (reasonByValue[i.reasonValue] && reasonByValue[i.reasonValue].title) || '';
		const quantity = Number(i.quantity) || 0;
		const lineQty = Number(i.lineQty) || 0;
		// Resolve a per-unit price even on legacy docs that only stored a line `price`.
		const unitPrice = (Number(i.singleUnitPrice) || 0)
			|| ((Number(i.price) || 0) && lineQty ? (Number(i.price) / lineQty) : 0);
		// `price` in the external payload is the ORIGINAL line total (unit price × originally-purchased qty),
		// not the return-line total. This matches the real-world payload example.
		const originalLineTotal = unitPrice * lineQty;
		const netOfRefunds = Big(originalLineTotal || 0).minus(Number(i.priorRefunds) || 0).round(2);
		const refundableOnLine = netOfRefunds.gt(0) ? Number(netOfRefunds.toFixed(2)) : 0;
		return {
			returnreason: i.reasonValue || '',
			returntext: reasonText,
			thumbnail: i.thumbnail || '',
			price: money(originalLineTotal),
			line_amount_paid: money(originalLineTotal),
			qty: num(quantity),
			productdesc: i.name || '',
			productsku: i.sku || '',
			singleunitrrp: money(i.singleUnitRRP),
			returnsecondary: i.reasonSecondaryValue || '',
			returnsecondarytext: i.reasonSecondaryText || '',
			returnreasondetail: i.reasonDetail || '',
			lineqty: num(lineQty),
			weight: String(Number(i.weight) || 0),
			oversized: i.oversized ? 'true' : 'false',
			origin: origin,
			// What is still refundable on the ORIGINAL LINE: its total less refunds already issued
			// against it in Woo, never below zero. The same definition SURP uses
			// (apply_prior_refunds_to_return_lines in the SURP plugin), so a return reads the same to
			// BC whichever system lodged it. Deliberately NOT scaled by the quantity being returned —
			// SURP doesn't either; the returned qty travels separately as `qty`.
			//
			// This used to be `refundableAmount || originalLineTotal`. refundableAmount is set to 0
			// when AOS builds a return and never filled in, so every AOS-lodged line sent the full
			// line, ignoring earlier refunds: a $200 line with $50 already refunded went out as $200
			// refundable where SURP would send $150. The `||` also read a genuine 0 (fully refunded)
			// as "missing" and sent the whole line instead.
			//
			// Like for like on GST: in ABI's Woo `_line_total` is GST-INCLUSIVE (AOS writes it as
			// totals.line.subTotal.after, orders.methods.mapping.doop:261), and priorRefunds is the
			// refund lines' `_line_total`. `_line_tax` is the GST already inside it - adding it would
			// count GST twice. See docs/llm/0001_MEMORY.md. Big.js because this is a refund amount.
			refundable_amount: refundableOnLine,
			prior_refunds: Number((Number(i.priorRefunds) || 0).toFixed(2)),
		};
	});

	// Internal notification block, matching the structure Pipedream builds for SURP lodgements.
	// The JSReport script (abi-talos, ABI/Returns/Return-PDF-Script) uses `subject` verbatim as the
	// email subject and appends `productInfo` to a plain-text body that already states the customer
	// name, email, order number and reimbursement type.
	//
	// Three deliberate inconsistencies with the main payload above — they're the observed contract,
	// not oversights here:
	//  - `productInfo` is a JSON *string*, like the top-level productsInfo
	//  - its keys are camelCase (productSKU/productDesc), NOT the lowercased forms PHP's
	//    sanitize_array() yields for the main payload — this block is built in JS, never through PHP
	//  - `qty` is a Number, where the main payload sends it as a String
	const admin = {
		email: settings.adminEmail || '',
		// Static in the observed contract, so every lodgement lands under one subject. Kept
		// verbatim so AOS returns don't read differently to SURP's in the same inbox.
		subject: 'New Returns Email Received',
		productInfo: JSON.stringify(productsInfo.map(p => ({
			returnReason: p.returnreason,
			returnText: p.returntext,
			// Same figure as the main payload's `price`: the ORIGINAL line total, 2dp.
			price: p.price,
			qty: Number(p.qty) || 0,
			productDesc: p.productdesc,
			productSKU: p.productsku,
		})), null, 4),
	};

	const submittedAt = (returnDoc.submission && returnDoc.submission.submittedAt) || new Date();

	return {
		date: new Date(submittedAt).toISOString(),
		wooID: wooID,
		wooCustomerID: returnDoc.customer ? Number(returnDoc.customer) : null,
		email: returnDoc.customerEmail || '',
		confirmationEmail: returnDoc.confirmationEmail || returnDoc.customerEmail || '',
		customerName: returnDoc.customerName || '',
		notes: returnDoc.notes || '',
		reimbursementType: returnDoc.reimbursementType || '',
		returnType: returnDoc.returnType || 'staff_local_dropoff',
		// Credits the lodging staff member downstream. SURP's staff page sends the same field;
		// without it every AOS return looks anonymous in BC/Monday.
		initiated_by: returnDoc.initiatedBy || '',
		paidParcels: Number(returnDoc.paidParcels) || 0,
		totalWeight: Number(totalWeight.toFixed(2)),
		address1: address.address1 || '',
		address2: address.address2 || '',
		state: address.state || '',
		postcode: address.postcode || '',
		country: address.country || '',
		city: address.city || '',
		customerAddress: customerAddress,
		returnInstance: returnInstance,
		returnNumber: returnDoc.ref,
		version: '2.0',
		origin: origin,
		admin: admin,
		// BOTH derived from the same call, never one stored and one derived — otherwise a drifted
		// pair emits a payload that says "send" and "suppress" at once, and which one wins depends
		// on which field the reader happens to check.
		bypassEmail: emailMode(returnDoc) === 'no_email',
		// SURP's email-routing vocabulary. 'no_email' is ours: SURP's three values all send
		// something, so suppression had no representation. Both 'manual' and 'none' map to it —
		// from Pipedream's side they're identical (don't send), and which one it was only matters
		// to us. Sent ALONGSIDE bypassEmail so behaviour is unchanged until the Pipedream email
		// step reads this field; bypassEmail can be dropped once it does.
		email_mode: emailMode(returnDoc),
		productsInfo: JSON.stringify(productsInfo, null, 4),
	};
}

/**
* Parse an inbound date, falling back to now when it can't be read.
*
* `new Date(junk)` yields Invalid Date, which Mongoose rejects with a CastError — losing the whole
* lodgement over one malformed field, on the one path that is its only record.
*
* @param {*} value The raw date value from the payload
*
* @returns {Date} The parsed date, or the current time
*/
function parseDate(value) {
	if (!value) return new Date();
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}


/**
* Dig the rendered Return Statement PDF's URL out of whatever the submission endpoint returned.
*
* The workflow's own response is the only reliable handle on that PDF. Its S3 key is
* `<REGION>-<wooID>|<ISO at render time>` — built inside the workflow from a timestamp taken when
* the render runs — so it can't be reconstructed from anything stored here.
*
* Tolerant by design: the response has been observed as a bare URL string ($respond with the URL
* as the body) but travels through more than one workflow, so a wrapped object is just as likely.
* Checks the usual carrier keys, then one level down, and gives up rather than guessing.
*
* @param {*} response The parsed response body stored on `submission.response`
* @param {Number} [depth] Internal recursion guard
*
* @returns {String|null} The PDF URL, or null if the response doesn't carry one
*/
function extractPdfUrl(response, depth = 0) {
	// Accept a .pdf URL anywhere, or any URL pointing at the returns bucket — the key itself
	// carries no extension, so a bucket URL without one is still the PDF we want.
	const isPdfUrl = v => typeof v === 'string'
		&& /^https?:\/\//i.test(v)
		&& (/\.pdf(\?|$)/i.test(v) || /pdfshift-returns/i.test(v));

	if (isPdfUrl(response)) return response;
	if (depth > 3 || !response || typeof response !== 'object') return null;

	const carriers = ['pdfUrl', 'pdfURL', 'url', 'href', 'body', 'data', 'result', 'output'];
	const direct = carriers.map(k => response[k]).find(v => isPdfUrl(v));
	if (direct) return direct;

	return carriers
		.map(k => response[k])
		.filter(v => v && typeof v === 'object')
		.reduce((found, v) => found || extractPdfUrl(v, depth + 1), null);
}


/**
* Map a return's email choice onto SURP's `email_mode` payload vocabulary.
*
* SURP's own values (standard / cc_only / admin_only) all send something, so 'no_email' is added
* here for suppression — the case bypassEmail has always covered.
*
* EITHER signal suppressing is enough. That union is deliberate and matters for two reasons:
*
*  - Docs written before `emailMode` existed carry only the boolean, and checking `emailMode` first
*    would never see it: Mongoose fills the schema default on init, so a legacy doc reads back as
*    emailMode:'template' + bypassEmail:true and is indistinguishable from an explicit 'template'.
*  - The two fields are independently writable through the REST save, so they can drift.
*
* Erring towards suppression keeps the failure safe: the worst case is an email someone has to
* send by hand, rather than one reaching a customer who was marked "no email".
*
* @param {Object} returnDoc The (plain) return document
*
* @returns {String} 'standard' or 'no_email'
*/
function emailMode(returnDoc) {
	if (returnDoc.bypassEmail === true) return 'no_email';
	return (returnDoc.emailMode || 'template') === 'template' ? 'standard' : 'no_email';
}


/**
* Resolve a SURP return-reason key against our own admin-managed `return-reasons` list.
*
* The two systems don't share a vocabulary: the seeded AOS list uses `reason-not-fit-for-purpose`
* while SURP sends `not-fit-for-purpose` for the same reason. Storing SURP's raw key would leave
* a value that never resolves against db.lists, so `reasonText` comes back empty on any later
* render and reason-grouped reporting gets an unmatched bucket.
*
* Tried in order: exact value, `reason-`-prefixed value, then title match against SURP's own
* display text. Falls back to the raw value so an unmapped reason is preserved, not discarded.
*
* @param {String} value SURP's `returnreason` key
* @param {String} [text] SURP's `returntext` display string
* @param {Object} [reasonsList] The `return-reasons` list doc with `items[{title, value}]`
*
* @returns {{value: String, text: String}} Resolved AOS list value and display text
*/
function resolveReasonValue(value, text, reasonsList) {
	const raw = String(value || '');
	const items = (reasonsList || {}).items || [];
	if (!items.length) return {value: raw, text: String(text || '')};

	const byValue = _.keyBy(items, 'value');
	const norm = s => String(s || '').trim().toLowerCase();

	const match = byValue[raw]
		|| byValue[`reason-${raw}`]
		|| items.find(i => norm(i.title) === norm(text));

	return match
		? {value: match.value, text: match.title}
		: {value: raw, text: String(text || '')};
}


/**
* Parse an inbound canonical returns payload (the shape buildExternalPayload emits) back into
* AOS field names — the inverse of buildExternalPayload, used by the intake endpoint.
*
* Deliberately pure and resolution-free: it does NOT look up the source order or read refund
* state. The caller resolves `wooId` to an order and re-reads prior refunds from Woo, because
* those are fresher than whatever SURP saw when it lodged.
*
* NOT a lossless inverse. The contract carries no `instance` (the caller supplies it out of
* band), no `image`, and `price` is the original line total rather than a unit price — so a
* build → parse → build round trip won't be bit-exact for prices that don't divide cleanly.
*
* @param {Object} payload The raw inbound payload
* @param {Object} [options] Additional options to mutate behaviour
* @param {Object} [options.reasonsList] The `return-reasons` list doc, for vocabulary mapping
*
* @returns {Object} Normalised fields ready for the caller to resolve and persist
*/
function parseExternalPayload(payload, options) {
	const settings = {reasonsList: null, ...options};
	if (!payload) throw new Error('No payload to parse');

	// Dispatch on the contract version and fail loudly. The payload is shared with SURP and will
	// drift; a hard failure beats silently mis-parsing a field that feeds refund amounts.
	const version = String(payload.version || '');
	if (version !== '2.0') throw new Error(`Unsupported returns payload version "${version || 'none'}"`);

	let productsInfo;
	try {
		// productsInfo is a JSON *string* in the canonical contract, not an array.
		productsInfo = typeof payload.productsInfo === 'string'
			? JSON.parse(payload.productsInfo || '[]')
			: (payload.productsInfo || []);
	} catch (e) {
		throw new Error(`Unparseable productsInfo: ${e.message}`, {cause: e});
	}
	if (!Array.isArray(productsInfo)) throw new Error('productsInfo did not parse to an array');

	// The Woo order-line id is absent from productsInfo — it only appears in refund_diagnostic,
	// keyed by `sku|line_total` (the same key SURP matches on). Ambiguous keys resolve to null so
	// we leave itemRef blank rather than attach a line to the wrong one.
	const itemRefByQueueKey = {};
	((payload.refund_diagnostic || {}).order_items || []).forEach(oi => {
		if (!oi || !oi.queue_key) return;
		itemRefByQueueKey[oi.queue_key] = Object.prototype.hasOwnProperty.call(itemRefByQueueKey, oi.queue_key)
			? null
			: String(oi.order_item_id || '');
	});

	const items = productsInfo.map(p => {
		// PHP's sanitize_array() lowercases productInfo keys, so accept both casings (SURP's own
		// consumers do the same — see its skill doc's camelCase/lowercase trap).
		const sku = String(p.productsku || p.productSKU || '');
		const lineQty = Number(p.lineqty || p.lineQty) || 0;
		const originalLineTotal = Number(p.price) || 0;
		// `price` is the ORIGINAL line total (unit price x originally-purchased qty), so divide
		// back out. Big.js rather than float division — this is money (RULES.md #13).
		const singleUnitPrice = lineQty > 0
			? Number(new Big(originalLineTotal).div(lineQty).toFixed(2))
			: 0;
		const reason = resolveReasonValue(p.returnreason, p.returntext, settings.reasonsList);

		return {
			itemRef: itemRefByQueueKey[`${sku}|${originalLineTotal}`] || '',
			sku,
			name: p.productdesc || '',
			quantity: Number(p.qty) || 0,
			lineQty,
			singleUnitPrice,
			singleUnitRRP: Number(p.singleunitrrp) || 0,
			weight: Number(p.weight) || 0,
			oversized: String(p.oversized) === 'true',
			thumbnail: p.thumbnail || '',
			reasonValue: reason.value,
			reasonText: reason.text,
			reasonSecondaryValue: p.returnsecondary || '',
			reasonSecondaryText: p.returnsecondarytext || '',
			reasonDetail: p.returnreasondetail || '',
			refundableAmount: Number(p.refundable_amount) || 0,
			// priorRefunds/priorRefundQty intentionally NOT taken from the payload — Woo is the
			// sole source of truth and the caller re-reads it via fetchPriorRefunds().
		};
	});

	return {
		ref: String(payload.returnNumber || ''),
		grocketId: String(payload.grocket_id || ''),
		wooId: payload.wooID ? String(payload.wooID) : '',
		customer: payload.wooCustomerID ? String(payload.wooCustomerID) : '',
		customerName: payload.customerName || '',
		customerEmail: payload.email || '',
		confirmationEmail: payload.confirmationEmail || payload.email || '',
		notes: payload.notes || '',
		reimbursementType: payload.reimbursementType || '',
		returnType: payload.returnType || '',
		origin: payload.origin || '',
		initiatedBy: payload.initiated_by || '',
		// SURP's cc_only / admin_only still send an email, so they're 'template' to us — the
		// customer was contacted. Only explicit suppression maps to 'none'. The exact upstream
		// value isn't lost: the raw payload is kept verbatim in submission.payload.
		emailMode: (payload.email_mode === 'no_email' || payload.bypassEmail === true)
			? 'none'
			: 'template',
		paidParcels: Number(payload.paidParcels) || 0,
		// An unparseable date must not cost us the lodgement: Invalid Date reaches Mongoose as a
		// CastError and rejects an otherwise-valid record that nothing else holds a copy of.
		submittedAt: parseDate(payload.date),
		address: {
			address1: payload.address1 || '',
			address2: payload.address2 || '',
			city: payload.city || '',
			state: payload.state || '',
			postcode: payload.postcode || '',
			country: payload.country || '',
		},
		items,
		// totalWeight / customerAddress / returnInstance are all derived from the above — not
		// stored, so they can't drift from the values we'd recompute.
	};
}


/**
* Look up prior refunds from a Woo order's MySQL data, PER ORDER LINE.
* SERVER-ONLY: relies on `app.dbwp[instance]` (Knex). Safe to keep in this isomorphic file because
* the client wrapper (`returnsUtils.vue`) never invokes it.
*
* Per line, not per SKU. This used to GROUP BY sku, so a SKU appearing on two lines of one order
* handed each line the refunds of both - a $100 refund against line 1 also came off line 2. SURP
* sums refunds per order item (_refunded_item_id) for exactly this reason; this now does the same,
* and matchPriorRefunds() pairs these lines with the return's items.
*
* Every line_item on the order is returned, refunded or not: knowing a SKU sits on two lines, one
* of them untouched, is what stops the untouched one inheriting the other's refunds.
*
* @param {Object} opts
* @param {Object} opts.app The global app object (so we can reach app.dbwp[instance])
* @param {String} opts.instance Instance code (AU/NZ/UK)
* @param {String|Number} opts.wooOrderId The original Woo order ID (parent post)
* @returns {Promise<Array<{id: number, sku: string, qty: number, total: number, refundedQty: number, refundedAmount: number}>>}
*/
function fetchPriorRefunds({app, instance, wooOrderId}) {
	if (!wooOrderId) return Promise.resolve([]);
	const dbwp = app && app.dbwp && app.dbwp[instance];
	if (!dbwp) return Promise.reject(new Error(`No Woo DB connection for instance "${instance}"`));

	return dbwp.raw(`
		SELECT
			line.order_item_id AS id,
			(SELECT pm.meta_value FROM wp_postmeta pm
				WHERE pm.post_id = pid.meta_value
				  AND pm.meta_key = '_sku'
				LIMIT 1) AS sku,
			CAST(qty_meta.meta_value AS DECIMAL(10,2)) AS qty,
			CAST(total_meta.meta_value AS DECIMAL(10,2)) AS total,
			COALESCE(refunded.qty, 0) AS refundedQty,
			COALESCE(refunded.amount, 0) AS refundedAmount
		FROM wp_woocommerce_order_items line
		JOIN wp_woocommerce_order_itemmeta pid
			ON pid.order_item_id = line.order_item_id
		   AND pid.meta_key = '_product_id'
		LEFT JOIN wp_woocommerce_order_itemmeta qty_meta
			ON qty_meta.order_item_id = line.order_item_id
		   AND qty_meta.meta_key = '_qty'
		LEFT JOIN wp_woocommerce_order_itemmeta total_meta
			ON total_meta.order_item_id = line.order_item_id
		   AND total_meta.meta_key = '_line_total'
		LEFT JOIN (
			SELECT
				refunded_item_id_meta.meta_value AS original_item_id,
				SUM(ABS(CAST(rqty.meta_value AS DECIMAL(10,2)))) AS qty,
				SUM(ABS(CAST(rtotal.meta_value AS DECIMAL(10,2)))) AS amount
			FROM wp_posts refund
			JOIN wp_woocommerce_order_items refund_item
				ON refund_item.order_id = refund.ID
			   AND refund_item.order_item_type = 'line_item'
			JOIN wp_woocommerce_order_itemmeta refunded_item_id_meta
				ON refunded_item_id_meta.order_item_id = refund_item.order_item_id
			   AND refunded_item_id_meta.meta_key = '_refunded_item_id'
			LEFT JOIN wp_woocommerce_order_itemmeta rqty
				ON rqty.order_item_id = refund_item.order_item_id
			   AND rqty.meta_key = '_qty'
			LEFT JOIN wp_woocommerce_order_itemmeta rtotal
				ON rtotal.order_item_id = refund_item.order_item_id
			   AND rtotal.meta_key = '_line_total'
			WHERE refund.post_type = 'shop_order_refund'
			  AND refund.post_parent = ?
			GROUP BY refunded_item_id_meta.meta_value
		) refunded
			ON refunded.original_item_id = line.order_item_id
		WHERE line.order_id = ?
		  AND line.order_item_type = 'line_item'
	`, [wooOrderId, wooOrderId])
		.then(rows => {
			// Knex+mysql returns [rows, fields]
			const dataRows = Array.isArray(rows) && Array.isArray(rows[0]) ? rows[0] : rows;
			return (dataRows || [])
				.filter(r => r.sku)
				.map(r => ({
					id: Number(r.id),
					sku: String(r.sku),
					qty: Number(r.qty) || 0,
					total: Number(r.total) || 0,
					refundedQty: Number(r.refundedQty) || 0,
					refundedAmount: Number(r.refundedAmount) || 0,
				}));
		});
}


/**
* Pair each return item with the Woo order line it came from, and read that line's prior refunds.
*
* AOS keeps no link from its order items to Woo's order_item_id, so lines are matched the way SURP
* matches them (apply_prior_refunds_to_return_lines): by SKU, then by line total. In order:
*
*   1. The only line on the order carrying that SKU - the common case, and the one that does not
*      depend on the line total at all (AOS and Woo can disagree on whether that includes GST).
*   2. Of several, the unclaimed one whose total is within a cent of the item's.
*   3. Of several, the unclaimed one whose ordered quantity matches the item's.
*   4. Still ambiguous: no prior refunds, and `ambiguous: true` so the caller can say so. SURP
*      falls back to the same (full line, nothing refunded) when it cannot match.
*
* Each line is claimed once, so two return items for the same SKU cannot both take one line.
*
* @param {Array} wooLines The result of fetchPriorRefunds()
* @param {Array<{sku, lineQty, lineTotal}>} items The return items, in order
* @returns {Array<{priorRefundQty: number, priorRefunds: number, ambiguous: boolean}>} One per item
*/
function matchPriorRefunds(wooLines, items) {
	const claimed = new Set();
	const none = ambiguous => ({priorRefundQty: 0, priorRefunds: 0, ambiguous});

	return (items || []).map(item => {
		const sku = String((item && item.sku) || '');
		const all = (wooLines || []).filter(l => l.sku === sku);
		const open = all.filter(l => !claimed.has(l.id));
		if (!open.length) return none(false);

		const total = Number(item.lineTotal) || 0;
		const qty = Number(item.lineQty) || 0;
		const line = (all.length === 1 && open[0])
			|| (total && open.find(l => Math.abs(l.total - total) < 0.01))
			|| (qty && open.filter(l => l.qty === qty).length === 1 && open.find(l => l.qty === qty))
			|| null;
		if (!line) return none(true);

		claimed.add(line.id);
		return {
			priorRefundQty: line.refundedQty,
			priorRefunds: Number(new Big(line.refundedAmount).toFixed(2)),
			ambiguous: false,
		};
	});
}

module.exports = {
	// Exported for returnsMonday, which now sends the email outcome to the board at arrival.
	// Shared rather than re-derived: this mapping decides what the customer was told, and a second
	// copy would be one deploy away from disagreeing with the payload SURP receives.
	emailMode,
	returnStatusLabels,
	returnStatusOrder,
	returnStatusLabel,
	returnTypeLabels,
	returnTypeLabel,
	buildExternalPayload,
	parseExternalPayload,
	extractPdfUrl,
	resolveReasonValue,
	findBestMatches,
	nextReturnNumber,
	fetchPriorRefunds,
	matchPriorRefunds,
	escapeRegExp,
};
